import * as Sentry from '@sentry/gatsby';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import * as ROUTES from '@constants/routes';

import { SessionActions, SessionContext } from '@contexts/index';

import { getQueryParams } from '@utils/url';

import {
  useExchangeHandoffTokenMutation,
  useLoginMutation,
  useRespondToAuthChallengeMutation,
} from 'generated/session.generated';
import type { AuthChallenge } from 'generated/types';

import { useNavigate } from './useNavigate';
import { useSnackbar } from './useSnackbar';

const messages = defineMessages({
  tempPasswordExpired: {
    id: 'loginPage.tempPasswordExpired',
    defaultMessage:
      'Your temporary password has expired, a new one has been emailed to you.',
  },
});
export function useSession() {
  const [signIn] = useLoginMutation();
  const [exchangeHandoffToken] = useExchangeHandoffTokenMutation();
  const [respondToAuthenticationChallenge] =
    useRespondToAuthChallengeMutation();
  const { navigateRoute } = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const context = React.useContext(SessionContext);
  const { formatMessage } = useIntl();
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (context === undefined) {
    throw new Error(`useSession must be used within a SessionProvider`);
  }

  const {
    state,
    dispatch,
    initializeSession,
    logout,
    clearStorage,
    isLoadingAuth,
    activeProfile,
    loggedInProfile,
    allProfiles,
    openReactivationDialog,
  } = context;
  const { logged_in_user_id } = state;
  const [redirectUri, setRedirectUri] = React.useState<
    string | string[] | undefined
  >();

  React.useEffect(() => {
    const { redirect_uri } = getQueryParams();
    setRedirectUri(redirect_uri);
  }, []);

  // Capture additional information in Sentry about the auth'd user
  React.useEffect(() => {
    Sentry.setUser(
      loggedInProfile?.user_id
        ? {
            username: loggedInProfile.user_id,
          }
        : null
    );
    Sentry.setExtra('active_profile_id', activeProfile?.user_id);
  }, [activeProfile?.user_id, loggedInProfile?.user_id]);

  const login = async (username: string, password: string) => {
    await clearStorage();

    const response = await signIn({
      variables: {
        input: {
          username,
          password,
        },
      },
    });
    if (response.errors?.length || !response.data?.login) {
      throw response.errors;
    }

    const { id, user, access, challenge } = response.data.login;
    if (user && id && access) {
      await initializeSession(user.user_id, id, access);
    }
    if (challenge) {
      return handleChallenge(challenge, username);
    }
    return navigate(`${ROUTES.LOADING}?redirect_uri=${redirectUri ?? ''}`);
  };

  /**
   * Trade a single-use SSO handoff nonce from VITALL Scan for a web session.
   *
   * Everything from the tokens onward is what `login` does, so a handoff lands
   * the user in the same place a password login would, challenges included.
   * The nonce is spent by the first attempt either way, so nothing here retries.
   */
  const handoff = async (token: string) => {
    await clearStorage();

    const response = await exchangeHandoffToken({
      variables: {
        input: { token },
      },
    });
    if (response.errors?.length || !response.data?.exchangeHandoffToken) {
      throw response.errors;
    }

    const { id, user, access, challenge } = response.data.exchangeHandoffToken;
    // A handoff either produces a session or it does not. Unlike a password
    // login there is no half-authenticated state to carry into a challenge, so
    // missing tokens mean the exchange failed.
    if (!user || !id || !access) {
      throw new Error('Handoff exchange returned no session');
    }
    await initializeSession(user.user_id, id, access);

    if (challenge) {
      return handleChallenge(challenge, user.email ?? undefined);
    }
    return navigate(`${ROUTES.LOADING}?redirect_uri=${redirectUri ?? ''}`);
  };

  const respondToAuthChallenge = async (
    challengeName: string,
    challengeResponses: Record<string, string>
  ) => {
    const response = await respondToAuthenticationChallenge({
      variables: {
        input: {
          challengeName,
          challengeResponses,
        },
      },
    });
    if (response.errors?.length || !response.data?.respondToAuthChallenge) {
      throw response.errors;
    }
    const { id, user, access, challenge } =
      response.data.respondToAuthChallenge;
    if (user && id && access) {
      await initializeSession(user.user_id, id, access);
    }
    if (challenge) {
      return handleChallenge(challenge, user?.email || undefined);
    }
    return navigate(`${ROUTES.LOADING}?redirect_uri=${redirectUri ?? ''}`);
  };

  const handleChallenge = (challenge: AuthChallenge, email?: string) => {
    const challengeName = challenge.name;
    if (challengeName === 'NEW_PASSWORD_REQUIRED' && email) {
      return navigateRoute(ROUTES.COMPLETE_NEW_PASSWORD, {
        email,
      });
    } else if (challengeName === 'PASSWORD_RESET_REQUIRED') {
      return navigate(ROUTES.FORGOT_PASSWORD);
    } else if (challengeName === 'TEMP_PASSWORD_EXPIRED') {
      enqueueSnackbar({
        message: formatMessage(messages.tempPasswordExpired),
        variant: 'info',
        isPersistent: true,
      });
      return;
    } else if (challengeName === 'DISABLED_USER' && email) {
      return openReactivationDialog(email);
      return;
    } else if (
      (challengeName === 'SOFTWARE_TOKEN_MFA' ||
        challengeName === 'SMS_MFA' ||
        challengeName === 'EMAIL_OTP') &&
      email
    ) {
      const challengeParameters = challenge.parameters;
      return navigateRoute(
        ROUTES.LOGIN_MFA,
        { redirect_uri: redirectUri },
        {
          state: {
            email,
            challengeName,
            ...(challengeName === 'SMS_MFA' && {
              phoneNumber: challengeParameters?.CODE_DELIVERY_DESTINATION,
            }),
            ...(challengeName === 'EMAIL_OTP' && {
              emailDestination: challengeParameters?.CODE_DELIVERY_DESTINATION,
            }),
          },
        }
      );
    } else if (challengeName === 'MFA_SETUP' && email) {
      return navigateRoute(ROUTES.SETUP_MFA, {
        ...challenge.parameters,
        email,
      });
    } else {
      throw new Error(`Unknown auth challenge: ${challengeName}`);
    }

    // Login success
    // ?.loginStatus === 'account-normal'

    // TODO Mfa logic
    // if (data.login.mfaEnabled) {
    //   sendMfaText();
    //   navigate(ROUTES.LOGIN_MFA, {
    //     state: {
    //      redirect_uri,
    //       phone: data.login.mfaNumber,
    //       email: data.login.loginId,
    //       firstName: data.login.firstName,
    //     },
    //   });
    // } else {
    //   navigate(`${ROUTES.LOADING}?redirect_uri=${redirect_uri ?? ''}`);
    // }
  };
  const changeActiveProfile = (user_id: string) => {
    dispatch({
      type: SessionActions.SetActiveProfile,
      active_user_id: user_id,
    });
  };

  // Reset active profile back to logged in profile
  const resetActiveProfile = () => {
    if (logged_in_user_id) {
      dispatch({
        type: SessionActions.SetActiveProfile,
        active_user_id: logged_in_user_id,
      });
    }
  };

  return {
    state: {
      ...state,
      activeProfile,
      allProfiles,
      loggedInProfile,
      isLoadingAuth,
      isVitall: state.isAdmin || state.isCurator,
      isOrg: state.isOrgAdmin || state.isPractitioner,
    },
    dispatch,
    logout,
    login,
    handoff,
    respondToAuthChallenge,
    resetActiveProfile,
    setActiveProfile: changeActiveProfile,
  };
}
